import React from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import {
  useGetSupportConfigQuery,
  useUpdateSupportConfigMutation,
} from "../../store/services/support";
import { UpdateSupportConfig } from "../../models/support";
import { useTenantScope } from "../../hooks/useTenantScope";

const getApiError = (e: any): string =>
  e?.data?.error?.message || "Ocurrió un error";

interface ConfigFormValues {
  support_email: string;
  terms_url: string;
  max_featured_faqs: number | null;
  confirmation_message: string;
}

const configValidationSchema = Yup.object().shape({
  support_email: Yup.string().email("Ingresá un email válido (ej: soporte@tuclub.com)"),
  terms_url: Yup.string().url("Ingresá una URL válida (ej: https://tuclub.com/terminos)"),
  max_featured_faqs: Yup.number()
    .typeError("Ingresá un número entre 1 y 10")
    .required("La cantidad máxima de destacadas es requerida")
    .min(1, "El mínimo es 1")
    .max(10, "El máximo es 10"),
  confirmation_message: Yup.string().max(
    500,
    "El mensaje no puede superar los 500 caracteres"
  ),
});

export function SupportConfig() {
  const tenantScope = useTenantScope();
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  const { data: config, isLoading } = useGetSupportConfigQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });
  const [updateConfig, { isLoading: isSaving }] = useUpdateSupportConfigMutation();

  const formik = useFormik<ConfigFormValues>({
    initialValues: {
      support_email: config?.supportEmail || "",
      terms_url: config?.termsUrl || "",
      max_featured_faqs: config?.maxFeaturedFaqs ?? null,
      confirmation_message: config?.confirmationMessage || "",
    },
    enableReinitialize: true,
    validationSchema: configValidationSchema,
    onSubmit: (values) => {
      const payload: UpdateSupportConfig = {
        support_email: values.support_email.trim() || undefined,
        terms_url: values.terms_url.trim() || undefined,
        max_featured_faqs: values.max_featured_faqs ?? undefined,
        confirmation_message: values.confirmation_message.trim() || undefined,
      };

      updateConfig(payload)
        .unwrap()
        .then(({ warning }) => {
          toast.success("Configuración guardada");
          if (warning) {
            toast(warning, { icon: "⚠️" });
          }
        })
        .catch((e) => toast.error(getApiError(e)));
    },
  });

  if (tenantScope.loading || (!needsTenantSelection && isLoading)) {
    return (
      <div className="grid">
        <div className="col-12">
          <div className="p-card p-4">
            <div className="flex flex-column justify-content-center align-items-center p-5">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" />
              <p className="text-600 mt-3 m-0">Cargando configuración...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid">
      <div className="col-12">
        <div className="p-card p-4">
          <div className="flex align-items-center gap-2 mb-4 pb-3 border-bottom-1 surface-border">
            <i className="pi pi-cog text-primary text-xl"></i>
            <h2 className="text-2xl font-semibold text-900 m-0">Configuración de soporte</h2>
          </div>

          {needsTenantSelection ? (
            <Message
              severity="warn"
              className="w-full justify-content-start"
              text="Seleccioná un tenant en el selector superior para ver y editar su configuración de soporte"
            />
          ) : (
            <>
              {config && !config.supportEmail && (
                <Message
                  severity="warn"
                  className="w-full mb-4 justify-content-start"
                  text="Sin email configurado. En la app se usa el email por defecto (soporte@grups.app) hasta que guardes uno."
                />
              )}

              <form onSubmit={formik.handleSubmit} className="formgrid grid">
                <div className="field col-12 md:col-6">
                  <label htmlFor="support_email" className="font-medium text-900">
                    Email de soporte
                  </label>
                  <InputText
                    id="support_email"
                    name="support_email"
                    value={formik.values.support_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSaving}
                    className={`w-full mt-2 ${
                      formik.touched.support_email && formik.errors.support_email
                        ? "p-invalid"
                        : ""
                    }`}
                    placeholder="soporte@tuclub.com"
                  />
                  {formik.touched.support_email && formik.errors.support_email && (
                    <small className="p-error block mt-1">
                      {formik.errors.support_email}
                    </small>
                  )}
                  <small className="text-600 block mt-1">
                    A esta casilla llegan los mensajes del formulario de contacto.
                  </small>
                </div>

                <div className="field col-12 md:col-6">
                  <label htmlFor="terms_url" className="font-medium text-900">
                    URL de términos y condiciones
                  </label>
                  <InputText
                    id="terms_url"
                    name="terms_url"
                    value={formik.values.terms_url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSaving}
                    className={`w-full mt-2 ${
                      formik.touched.terms_url && formik.errors.terms_url
                        ? "p-invalid"
                        : ""
                    }`}
                    placeholder="https://tuclub.com/terminos"
                  />
                  {formik.touched.terms_url && formik.errors.terms_url && (
                    <small className="p-error block mt-1">{formik.errors.terms_url}</small>
                  )}
                </div>

                <div className="field col-12 md:col-6">
                  <label htmlFor="max_featured_faqs" className="font-medium text-900 block">
                    Máximo de preguntas destacadas *
                  </label>
                  <InputNumber
                    inputId="max_featured_faqs"
                    name="max_featured_faqs"
                    value={formik.values.max_featured_faqs}
                    onValueChange={(e: InputNumberValueChangeEvent) =>
                      formik.setFieldValue("max_featured_faqs", e.value ?? null)
                    }
                    onBlur={() => formik.setFieldTouched("max_featured_faqs", true)}
                    disabled={isSaving}
                    min={1}
                    max={10}
                    className="mt-2"
                    inputClassName={`text-center ${
                      formik.touched.max_featured_faqs && formik.errors.max_featured_faqs
                        ? "p-invalid"
                        : ""
                    }`}
                    inputStyle={{ width: "7rem" }}
                  />
                  {formik.touched.max_featured_faqs && formik.errors.max_featured_faqs && (
                    <small className="p-error block mt-1">
                      {formik.errors.max_featured_faqs}
                    </small>
                  )}
                  <small className="text-600 block mt-2">
                    Cantidad máxima de artículos destacados en la home de ayuda (entre 1 y 10).
                  </small>
                </div>

                <div className="field col-12">
                  <label htmlFor="confirmation_message" className="font-medium text-900">
                    Mensaje de confirmación
                  </label>
                  <InputTextarea
                    id="confirmation_message"
                    name="confirmation_message"
                    value={formik.values.confirmation_message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSaving}
                    rows={4}
                    className={`w-full mt-2 ${
                      formik.touched.confirmation_message &&
                      formik.errors.confirmation_message
                        ? "p-invalid"
                        : ""
                    }`}
                    placeholder="Ej: ¡Gracias por escribirnos! Te vamos a responder dentro de las próximas 48 hs."
                  />
                  {formik.touched.confirmation_message &&
                    formik.errors.confirmation_message && (
                      <small className="p-error block mt-1">
                        {formik.errors.confirmation_message}
                      </small>
                    )}
                  <small className="text-600 block mt-1">
                    Opcional (máx. 500 caracteres). Se muestra al usuario después de enviar el
                    formulario de contacto.
                  </small>
                </div>

                <div className="field col-12">
                  <div className="flex flex-wrap justify-content-center pt-3">
                    <Button
                      icon="pi pi-check"
                      severity="success"
                      type="submit"
                      label="Guardar"
                      loading={isSaving}
                    />
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
